import { canv } from "@/canv/global";
import {
    getColorProperty,
    getContrastColor,
    getNumberProperty,
    getStringProperty,
} from "@/canv/utils";
import { ElementBase } from "@/elementBase";
import { regenerateTextPartUID } from "@/elements/text/textPartFactory";
import { widgetOption } from "@/widgetMenu/widgetMenu";
import { TextWidgetV2 } from "../text/textV2";
import type { TextAlign, TextVerticalAlign } from "../text/typesV2";
import { STICKER_TEXT_FIT_THRESHOLD_PX } from "./constants";
import type { StickerWidgetStateFull } from "./types";

export class StickerWidget extends ElementBase {
    constructor(startX: number, startY: number, color: string) {
        super();
        this.startX = startX;
        this.startY = startY;
        this.width = 250;
        this.height = 250;
        this.color = color; //"#FFC700";

        // Тут ставим не правильный startY, который поправим в первом же Draw
        this.textPart = new TextWidgetV2(this.startX + this.padding, this.startY + this.padding);

        this.textPart.setResizeMode("sticker");
        this.textPart.setEditingAutosaveHandler(() => {
            canv.eventManager.openRecord();
            this.saveState("textFocus");
            canv.eventManager.closeRecord();
            this.fixState("textFocus");
        });

        this.textPart.setKeepMinWidth(false);
        this.textPart.hardWidth = this.width - 2 * this.padding;
        this.textPart.hardHeight = this.height - 2 * this.padding;
        this.textPart.menuTextAlign("center");
        this.textPart.menuTextVerticalAlign("center");
        this.textPart.updateDims();

        this.updateDims();
    }

    resize(x: number, y: number, catchPointer: string) {
        if (["sideRight", "sideBottom", "sideLeft", "sideTop"].includes(catchPointer)) return;

        const diff = Math.abs(x) > Math.abs(y) ? x : y;

        if (this.height + diff <= 15) return;

        const widthKoef = this.width / this.height;

        this.height += diff;
        this.width += widthKoef * diff;
        this.updateDims();
    }

    makeWide() {
        this.width = 1.8 * this.height;
        this.updateDims();
    }

    makeNormal() {
        this.width = this.height;
        this.updateDims();
    }

    regenerateUIDS() {
        regenerateTextPartUID(this.textPart, this.UID);
    }

    updateDims(onlyPosition = false) {
        const isTextEditing = this.textPart.getIsEditing();

        if (!onlyPosition) {
            this.padding = 0.1 * this.height;
            this.textPart.hardWidth = this.width - 2 * this.padding;
            this.textPart.hardHeight = this.height - 2 * this.padding;

            if (!isTextEditing) {
                this.scheduleStickerTextFit();
            }
        }

        this.textPart.startX = this.startX + this.width / 2 - this.textPart.width / 2;

        const textVerticalAlign = this.textPart.getData().textVerticalAlign;
        if (textVerticalAlign === "top") {
            this.textPart.startY = this.startY + this.textPart.lineHeight + this.padding / 2;
        } else if (textVerticalAlign === "bottom") {
            this.textPart.startY =
                this.startY +
                this.height +
                this.textPart.lineHeight -
                this.textPart.height -
                this.padding / 2;
        } else {
            this.textPart.startY =
                this.startY + this.height / 2 - this.textPart.height / 2 + this.textPart.lineHeight;
        }

        if (!isTextEditing) {
            this.textPart.updateDims();
        }

        const gap = this.height / 33;
        const bottomGap = this.height / 13;

        this.updateBase(
            this.startX - gap,
            this.startY - gap,
            this.width + 2 * gap,
            this.height + gap + bottomGap,
        );

        if (isTextEditing && canv.textEditingController.isEditingWidget(this.textPart)) {
            canv.textEditingController.syncAfterHostResize();
        }
    }

    scheduleStickerTextFit() {
        const signature = this.getStickerTextFitSignature();

        if (signature === this.appliedStickerTextFitSignature) {
            this.pendingStickerTextFit = false;
            return;
        }

        this.pendingStickerTextFit = true;

        if (this.stickerTextFitRafId !== null) {
            return;
        }

        this.stickerTextFitRafId = requestAnimationFrame(() => {
            this.stickerTextFitRafId = null;
            this.applyStickerTextFit();
        });
    }

    applyStickerTextFit(force = false) {
        if (!this.pendingStickerTextFit) {
            return;
        }

        const signature = this.getStickerTextFitSignature();
        if (!force && signature === this.appliedStickerTextFitSignature) {
            this.pendingStickerTextFit = false;
            return;
        }

        this.pendingStickerTextFit = false;
        this.textPart.fitSticker();
        this.textPart.updateDims();
        this.appliedStickerTextFitSignature = signature;
        this.updateDims(true);
        canv.redraw();
    }

    flushStickerTextFit() {
        if (this.stickerTextFitRafId !== null) {
            cancelAnimationFrame(this.stickerTextFitRafId);
            this.stickerTextFitRafId = null;
        }

        if (this.pendingStickerTextFit) {
            this.applyStickerTextFit(true);
        }
    }

    getStickerTextFitSignature() {
        const widthBucket =
            Math.round(this.textPart.hardWidth / STICKER_TEXT_FIT_THRESHOLD_PX) *
            STICKER_TEXT_FIT_THRESHOLD_PX;
        const heightBucket =
            Math.round(this.textPart.hardHeight / STICKER_TEXT_FIT_THRESHOLD_PX) *
            STICKER_TEXT_FIT_THRESHOLD_PX;
        return `${widthBucket}:${heightBucket}:${this.textPart.symbolsCount}`;
    }

    resizingStart() {
        this.pendingStickerTextFit = false;
        this.appliedStickerTextFitSignature = null;

        if (this.stickerTextFitRafId !== null) {
            cancelAnimationFrame(this.stickerTextFitRafId);
            this.stickerTextFitRafId = null;
        }

        this.textPart?.resizingStart?.();
    }

    resizingStop() {
        this.flushStickerTextFit();
        this.textPart?.resizingStop?.();
        // перерасчет при расширении вбок
        this.updateDims(true);
    }

    TryAction(type: string) {
        // TODO: костыль для релиза
        // нужно подмать как исправить асинхронность и тут и в других виджетах
        (async () => {
            await this.textPart.TryAction(type);

            this.updateDims();
        })();

        if (type === "keyboardDown") {
            this.updateDims();
            this.tryShortcut();
        }

        if (type === "mousedown" && !this.textPart.isOver()) {
            if (this.isOver()) {
                canv.toolBox.TryGetToolByName("cursorTool").turnMode("dragging");
            } else {
                canv.toolBox.TryGetToolByName("cursorTool").turnMode("");
                canv.toolBox.TryGetToolByName("cursorTool").TryAction("mousedown");
            }
        }
    }

    isInteraction(context: string, force = false) {
        if ((context === "focus" && this.isOver()) || force) {
            this.fixState("textFocus");
            this.textPart.focusText(context === "focus");
            return {
                type: "interaction",
            };
        }

        if (context === "insertText") {
            this.fixState("textFocus");
            if (canv.keyboard.isEnter()) {
                this.textPart.focusTextByEnter();
            } else if (!canv.keyboard.isEnter()) {
                this.textPart.focusText();
                canv.textEditingController.insertText(canv.keyboard.command, true);
                this.TryAction("keyboardDown");
            }
            this.updateDims();
            return {
                type: "interaction",
            };
        }

        if (context === "pastText") {
            this.fixState("textFocus");
            this.textPart.focusText();
            canv.textEditingController.pasteFromClipboard(true);

            return {
                type: "interaction",
            };
        }

        return { type: "none" };
    }

    stopInteraction() {
        const shouldSave = !this.textPart.consumeHostInteractionSaveSkip();

        if (shouldSave) {
            canv.eventManager.openRecord();
            this.saveState("textFocus");
            canv.eventManager.closeRecord();
        }

        this.textPart.unfocusText();
    }

    tryShortcut() {
        if (canv.keyboard.isEscCommand()) {
            // stopInteraction вызывается внутри cursorTool при изменении mode
            canv.toolBox.TryGetToolByName("cursorTool").turnMode("");
        }
    }

    draw() {
        canv.ctx.save();
        const screenWidth = (this.width * canv.m[0]) / canv.dpr;
        const screenHeight = (this.height * canv.m[3]) / canv.dpr;
        const drawShadow = Math.max(screenWidth, screenHeight) > 24;
        if (drawShadow) {
            canv.ctx.shadowColor = "rgba(99, 99, 99, 0.2)";
            // 250 - base height
            // 8 - blur size (250/30 ~ 8.3)
            // 0, 2 - offset x, offset y (250/125 = 2)
            canv.ctx.shadowBlur = canv.actionManager.getConstant(this.height / 30) * canv.m[3];
            canv.ctx.shadowOffsetX = 0;
            canv.ctx.shadowOffsetY = canv.actionManager.getConstant(this.height / 125) * canv.m[3];
        }
        canv.ctx.fillStyle = this.color;
        canv.ctx.fillRect(this.startX, this.startY, this.width, this.height);
        canv.ctx.restore();

        this.textPart.draw();
    }

    translate(x: number, y: number) {
        this.startX += x;
        this.startY += y;
        this.updateDims(true);
    }

    clearText() {
        this.textPart.clearText();
        this.updateDims();
    }

    searchText(text: string) {
        const stickerText = this.textPart.getText().toLowerCase();

        if (stickerText.includes(text)) {
            return [
                {
                    status: true,
                    textWidget: this.textPart,
                    mode: "widget",
                },
            ];
        }

        return [];
    }

    menuColor(color: string) {
        this.color = color;
        this.textPart.menuTextColor(getContrastColor(color), true);
        // update last used color in stickerTool
        const stickerColor = canv.toolBox.TryGetToolByName("stickerTool");

        stickerColor.setColor(this.color);
    }

    menuTextBold(value: boolean) {
        this.textPart.menuTextBold(value);
        this.updateDims();
    }

    menuTextStrike(value: boolean) {
        this.textPart.menuTextStrike(value);
        this.updateDims();
    }

    menuTextItalic(value: boolean) {
        this.textPart.menuTextItalic(value);
        this.updateDims();
    }

    menuTextUnderline(value: boolean) {
        this.textPart.menuTextUnderline(value);
        this.updateDims();
    }

    menuTextBulleted(value: boolean) {
        this.textPart.menuTextBulleted(value);
        this.updateDims();
    }

    menuTextAlign(value: TextAlign) {
        this.textPart.menuTextAlign(value);
        this.updateDims();
    }

    menuTextVerticalAlign(value: TextVerticalAlign) {
        this.textPart.menuTextVerticalAlign(value);
        this.updateDims();
    }

    menuLink(currentLink: string) {
        const isEditing = this.textPart.getIsEditing();

        if (!isEditing) {
            canv.toolBox.TryGetToolByName("cursorTool").setSelectedAndInteraction(this.parent);
            canv.textEditingController.selectAll();
        }

        this.textPart.menuTextLink(currentLink);
    }

    getWidgetOptions() {
        const widgetOptions: widgetOption[] = [];

        widgetOptions.push(new widgetOption("stickerColor", this.color, this.UID));

        const { isItalic, isUnderline, isBold, isStriked, hasBulletList } =
            this.textPart.styleSummary;

        widgetOptions.push(new widgetOption("textItalic", isItalic, this.UID));
        widgetOptions.push(new widgetOption("textUnderline", isUnderline, this.UID));
        widgetOptions.push(new widgetOption("textBold", isBold, this.UID));
        widgetOptions.push(new widgetOption("textStrike", isStriked, this.UID));
        widgetOptions.push(new widgetOption("textBulleted", hasBulletList, this.UID));

        widgetOptions.push(
            new widgetOption("textAlign", this.textPart.getData().textAlign, this.UID),
        );
        widgetOptions.push(
            new widgetOption(
                "textVerticalAlign",
                this.textPart.getData().textVerticalAlign,
                this.UID,
            ),
        );

        widgetOptions.push(new widgetOption("link", this.textPart.styleSummary.link, this.UID));

        return widgetOptions;
    }

    getHandlerForOption(name: string) {
        if (name === "stickerColor") {
            return this.menuColor;
        } else if (name === "textBold") {
            return this.menuTextBold;
        } else if (name === "textStrike") {
            return this.menuTextStrike;
        } else if (name === "textItalic") {
            return this.menuTextItalic;
        } else if (name === "textUnderline") {
            return this.menuTextUnderline;
        } else if (name === "textBulleted") {
            return this.menuTextBulleted;
        } else if (name === "link") {
            return this.menuLink;
        } else if (name === "textAlign") {
            return this.menuTextAlign;
        } else if (name === "textVerticalAlign") {
            return this.menuTextVerticalAlign;
        }
    }

    getFixType(reason: string) {
        let fixType = "full";

        switch (reason) {
            case "move":
                fixType = "move";
                break;
            case "resize":
                fixType = "resize";
                break;
            case "wm_stickerColor":
                fixType = "color";
                break;
            case "wm_textBold":
            case "wm_textStrike":
            case "wm_textBulleted":
                fixType = "full";
                break;
        }

        return fixType;
    }

    getState(fixType: string) {
        switch (fixType) {
            case "move":
                return {
                    startX: this.startX,
                    startY: this.startY,
                };
            case "resize":
                return {
                    startX: this.startX,
                    startY: this.startY,
                    width: this.width,
                    height: this.height,
                };
            case "color":
                return {
                    color: this.color,
                };
            case "bold":
                return {
                    textPart: this.textPart.getState("full"),
                };
            case "full":
                return {
                    UID: this.UID,
                    parentUID: this.parentUID,
                    entityType: "stickerWidget",
                    color: this.color,
                    height: this.height,
                    width: this.width,
                    padding: this.padding,
                    startX: this.startX,
                    startY: this.startY,
                    textPart: this.textPart.getState("full"),
                };
        }
    }

    loadState(stateObj: StickerWidgetStateFull, fixType: string) {
        switch (fixType) {
            case "move":
                this.startX = getNumberProperty("startX", stateObj);
                this.startY = getNumberProperty("startY", stateObj);
                break;
            case "resize":
                this.startX = getNumberProperty("startX", stateObj);
                this.startY = getNumberProperty("startY", stateObj);
                this.width = getNumberProperty("width", stateObj);
                this.height = getNumberProperty("height", stateObj);
                break;
            case "color":
                this.color = getColorProperty("color", stateObj);
                this.textPart.menuTextColor(getContrastColor(this.color), true);
                break;
            case "bold":
                this.textPart.loadState(stateObj.textPart, "full");
                break;
            case "full": {
                this.UID = getStringProperty("UID", stateObj);
                this.parentUID = getStringProperty("parentUID", stateObj);
                this.color = getColorProperty("color", stateObj);
                this.width = getNumberProperty("width", stateObj);
                this.height = getNumberProperty("height", stateObj);
                this.padding = getNumberProperty("padding", stateObj);
                this.startX = getNumberProperty("startX", stateObj);
                this.startY = getNumberProperty("startY", stateObj);
                this.textPart.setKeepMinWidth(false);
                this.textPart.loadState(stateObj.textPart, "full");
                this.textPart.setResizeMode("sticker");
                this.textPart.setEditingAutosaveHandler(() => {
                    canv.eventManager.openRecord();
                    this.saveState("textFocus");
                    canv.eventManager.closeRecord();
                    this.fixState("textFocus");
                });

                this.textPart.menuTextColor(getContrastColor(this.color), true);
                break;
            }
        }

        this.updateDims();
    }

    constructorName: "sticker" = "sticker";
    startX = 0;
    startY = 0;
    width = 0;
    height = 0;

    textPart: TextWidgetV2;
    padding = 25;

    color: string;
    stickerTextFitRafId: number | null = null;
    pendingStickerTextFit = false;
    appliedStickerTextFitSignature: string | null = null;
}
